import { useOnlineStatus } from './offline-misc';
import { useApolloClient, useMutation, gql } from '@apollo/client';
import { useCachedMutations } from './offline-misc';
import { v4 as uuidv4 } from 'uuid';
import { loader } from 'graphql.macro';
import { useCurrentUser } from '../../providers/UserProvider';
import produce from 'immer';
import axios from 'axios';
import { CF_DOMAIN } from '../../constants/aws';
import moment from 'moment';
import { useWorkspace } from '../../providers/WorkspaceProvider';
import { useTemplates } from '../../providers/TemplateProvider';
import { useUpdateVehicle } from './vehicleMutation';
import { addOfflineFile } from './localForageDb';
import { showToast } from '../../graphql/cache/modal';
import { toastVariant, toastLength } from '../../constants/misc';
import { useNavigate } from 'react-router-dom';
import { getRoute, paths } from '../../constants/strings';
import { useWorkspacePermissions } from '../../providers/WorkspacePermissionsProvider';
import { useMyInspections } from '../../providers/MyInspectionsProvider';
import {
  convertHeicIfNeeded,
  getFileExtension,
} from '../../components/image_uploads/utils';

const inspectionMutation = loader(
  '../../components/modals/provider_modals/CreateAssessmentModal.inspection.graphql',
);

const inspectionNotesMutation = loader(
  '../../pages/inspection_page/Inspection.updateNotes.graphql',
);

const inspectionQuery = loader(
  '../../pages/inspection_page/Inspection.inspection.graphql',
);

const publishMutation = loader(
  '../../pages/inspection_page/Inspection.complete.graphql',
);

const inspectionTemplateFragment = loader(
  '../../pages/inspection_page/Inspection.inspectionTemplate.graphql',
);
const vehicleFragment = loader(
  '../../pages/fleet/Vehicle.vehicleFragment.graphql',
);

const userFragment = loader('./User.userFragment.graphql');

const notesFragment = loader('./InspectionNotesFragment.graphql');

const usageFragment = loader('./InspectionUsageFragment.graphql');

const inspectionItemMutation = loader(
  '../../components/inspections/inspection_items/InspectionItemText.graphql',
);

const inspectionItemUpdateMutation = loader(
  '../../components/inspections/inspection_items/InspectionItemText.updateInspection.graphql',
);

const deleteInspectionItemUsersMutation = loader(
  '../../components/inspections/inspection_items/InspectionItemInput.deleteUsers.graphql',
);

const addInspectionItemUsersMutation = loader(
  '../../components/inspections/inspection_items/InspectionItemInput.addUsers.graphql',
);

const uploadUrlQuery = loader('../../graphql/queries/s3.graphql');

const updateInspectionMutation = loader(
  '../../pages/inspection_page/Inspection.update.graphql',
);

const submitBundleMutation = loader(
  '../../pages/inspection_page/Inspection.bundle.graphql',
);

const deleteBundleMutation = loader(
  '../../pages/inspection_page/Inspection.deleteBundle.graphql',
);

const addParticipantMutation = loader(
  '../../pages/inspection_page/RightSidebar.addParticipant.graphql',
);

const addCategoryStatusMutation = loader(
  '../../pages/inspection_page/Inspection.addCatStatus.graphql',
);

//if anything changes then this variables can be chnaged here rather than in each of the write and read query
const returnInspectionQueryVaribales = (inspectionId) => {
  return {
    options: {
      filters: [
        { field: 'id', value: `${inspectionId}`, operator: 'eq' },
        { field: 'isArchived', operator: 'eq', value: ['FALSE'] },
      ],
    },
  };
};

// Compress an image file before storage or upload.
// - Resizes to fit within 1920×1920 (preserving aspect ratio)
// - Re-encodes as JPEG at 0.82 quality (PNG → PNG to preserve signatures/transparency)
// - Falls back to the original file silently if canvas is unavailable or throws
const compressImage = (file) =>
  new Promise((resolve) => {
    if (!file?.type?.startsWith('image/')) return resolve(file);

    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const MAX = 1920;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          const ratio = Math.min(MAX / width, MAX / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);

        const isPng = file.type === 'image/png';
        const outType = isPng ? 'image/png' : 'image/jpeg';
        const quality = isPng ? undefined : 0.82;

        canvas.toBlob(
          (blob) => {
            if (!blob || blob.size >= file.size) return resolve(file);
            resolve(
              new File([blob], file.name, {
                type: outType,
                lastModified: Date.now(),
              }),
            );
          },
          outType,
          quality,
        );
      } catch {
        resolve(file);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };

    img.src = url;
  });

export const useInspection = () => {
  const {
    workspace,
    workspaceVehicles,
    workspaceId: reportedWorkspaceId,
  } = useWorkspace();
  const online = useOnlineStatus();
  const client = useApolloClient();
  const { addMutation } = useCachedMutations();
  const updateAsset = useUpdateVehicle();
  const { allUsers } = useWorkspacePermissions();
  const { syncFromCache } = useMyInspections();

  //inspection addition
  const [addInspectionMutation] = useMutation(inspectionMutation);

  const [updateInspectionNotesMutation] = useMutation(inspectionNotesMutation);

  const [updateInspection] = useMutation(updateInspectionMutation);

  //inspection TEXT type question addition
  const [addInspectionItemMutation] = useMutation(inspectionItemMutation);

  const [deleteInspectionItemUsers] = useMutation(
    deleteInspectionItemUsersMutation,
    {
      refetchQueries: ['InspectionForInspectionCreation'],
    },
  );

  const [addInspectionItemUsers] = useMutation(addInspectionItemUsersMutation, {
    refetchQueries: ['InspectionForInspectionCreation'],
  });

  //inspection TEXT type question update
  const [updateInspectionItemMutation] = useMutation(
    inspectionItemUpdateMutation,
    {
      refetchQueries: ['InspectionForInspectionCreation'],
    },
  );

  const [publishInspection] = useMutation(publishMutation, {
    refetchQueries: ['InspectionForInspectionCreation'],
  });

  // bundle mutations
  const [submitBundle] = useMutation(submitBundleMutation, {
    refetchQueries: ['InspectionForInspectionCreation'],
  });

  const [deleteBundle] = useMutation(deleteBundleMutation, {
    refetchQueries: ['InspectionForInspectionCreation'],
  });

  //category status support
  const [addParticipantMut] = useMutation(addParticipantMutation);
  const [addCategoryStatusMut] = useMutation(addCategoryStatusMutation);

  const { user: currentUser } = useCurrentUser();
  const { templates } = useTemplates();
  const navigate = useNavigate();
  const updateBundle = async ({ inspectionId, categoryId, questions }) => {
    const options = {
      variables: {
        inspectionId,
        categoryId,
      },
    };

    if (online) {
      return submitBundle(options);
    }

    const cachedInspection = client.readQuery({
      query: inspectionQuery,
      variables: returnInspectionQueryVaribales(inspectionId),
    });

    if (!cachedInspection?.inspections?.[0]) {
      showToast({
        title: 'Action Unavailable Offline',
        message: 'A network connection is required to add bundle questions.',
        variant: toastVariant.danger,
        time: toastLength.lg,
      });
      return;
    }

    const newIds = [];

    const bundleId = uuidv4().toString();

    for (const question of questions) {
      const uuid = uuidv4().toString();

      const addInspectionItemMock = {
        __typename: 'InspectionItem',
        id: `${uuid}`,
        questionId: question.id,
        value: '',
        failed: false,
        status: '',
        failNotes: null,
        failImage: null,
        user: null,
        users: null,
        additional: null,
        impact: 0,
        probability: 0,
        dateModified: null,
        categoryId,
        bundleId,
        vehicle: null,
        comments: [],
        subject: null,
        customer: null,
        auditType: null,
        attachments: [],
        actionItemId: null,
      };

      const previousData = await client.readQuery({
        query: inspectionQuery,
        variables: returnInspectionQueryVaribales(inspectionId),
      });

      const newData = produce(previousData, (draft) => {
        draft.inspections[0].items = [
          ...draft.inspections[0].items,
          addInspectionItemMock,
        ];
      });

      client.writeQuery({
        query: inspectionQuery,
        variables: returnInspectionQueryVaribales(inspectionId),
        data: newData,
      });

      newIds.push(addInspectionItemMock.id);
    }

    const mockResult = {
      __typename: 'Inspection',
      id: inspectionId,
      items: newIds.map((id) => ({ id, categoryId, bundleId })),
    };

    const uuid = uuidv4().toString();

    await addMutation(
      submitBundleMutation,
      options,
      uuid,
      mockResult,
      ['InspectionForInspectionCreation'],
      '',
      true,
    );

    const inspection = await client.readQuery({
      query: inspectionQuery,
      variables: returnInspectionQueryVaribales(inspectionId),
    });
    return new Promise((resolve) => {
      const responseData = { data: { submitBundle: inspection } };
      resolve(responseData);
    });
  };

  const removeBundle = async ({ inspectionId, bundleId }) => {
    const options = {
      variables: {
        inspectionId,
        bundleId,
      },
    };

    if (online) {
      return deleteBundle(options);
    }

    const mockResult = {
      __typename: 'Inspection',
      id: inspectionId,
    };

    const uuid = uuidv4().toString();

    await addMutation(
      deleteBundleMutation,
      options,
      uuid,
      mockResult,
      ['InspectionForInspectionCreation'],
      '',
    );

    const oldData = await client.readQuery({
      query: inspectionQuery,
      variables: returnInspectionQueryVaribales(inspectionId),
    });

    const newData = produce(oldData, (draft) => {
      if (
        draft.inspections &&
        draft.inspections[0] &&
        draft.inspections[0].items
      ) {
        const foundInspectionItems = draft.inspections[0].items
          .map((obj) => obj.bundleId === bundleId && obj)
          .filter((i) => i);

        if (foundInspectionItems.length > 0) {
          foundInspectionItems.forEach((item) => {
            item.isArchived = true;
            item.dateModified = null;
          });

          draft.inspections[0].items = draft.inspections[0].items.filter(
            (item) => item.bundleId !== bundleId,
          );
        }
      }
    });

    client.writeQuery({
      query: inspectionQuery,
      variables: returnInspectionQueryVaribales(inspectionId),
      data: newData,
    });
  };

  const updateNotes = async (inspectionId, notes) => {
    const options = {
      variables: { id: inspectionId, notes },
    };
    if (online) {
      return updateInspectionNotesMutation(options);
    }

    const mockResult = {
      __typename: 'Inspection',
      id: inspectionId,
      notes,
    };

    const uuid = uuidv4().toString();

    await addMutation(
      inspectionNotesMutation,
      options,
      uuid,
      mockResult,
      [],
      'updateInspection.id',
    );

    const cacheId = `Inspection:${inspectionId}`;

    const inspection = client.cache.readFragment({
      id: cacheId,
      fragment: notesFragment,
      fragmentName: 'NotesFragment',
    });

    if (inspection) {
      const newInspection = { ...inspection, notes };

      client.cache.writeFragment({
        id: cacheId,
        fragment: notesFragment,
        data: newInspection,
      });
    }
  };

  const updateInspectionUsage = async ({
    inspectionId,
    odometer,
    hours,
    assetId,
  }) => {
    const options = {
      variables: { id: inspectionId, odometer, hours },
    };
    // don't need to handle offline stuff here because it already does on it's own
    await updateAsset({ id: assetId, hours, odometer });
    if (online) {
      return updateInspection(options);
    }

    const mockResult = {
      __typename: 'Inspection',
      id: inspectionId,
      hours,
      odometer,
    };

    const uuid = uuidv4().toString();

    await addMutation(
      updateInspectionMutation,
      options,
      uuid,
      mockResult,
      [],
      'updateInspection.id',
    );

    const cacheId = `Inspection:${inspectionId}`;

    const inspection = client.cache.readFragment({
      id: cacheId,
      fragment: usageFragment,
      fragmentName: 'UsageFragment',
    });

    if (inspection) {
      const newInspection = { ...inspection, hours, odometer };

      client.cache.writeFragment({
        id: cacheId,
        fragment: usageFragment,
        data: newInspection,
      });
    }
  };

  const addInspection = async ({
    templateId,
    vehicleId,
    inspectionType,
    participants,
    workspaceId,
    incidentId,
    timecardId,
    privateAssessment,
    subject,
    conditionalMetadata,
    location,
    anonymous,
    riskRegistryId,
  }) => {
    const options = {
      variables: {
        templateId,
        vehicleId,
        inspectionType,
        participants,
        workspaceId,
        incidentId,
        timecardId,
        private: privateAssessment,
        subject,
        conditionalMetadata,
        reportedWorkspaceId,
        location,
        anonymous,
        riskRegistryId,
      },
    };

    if (online) {
      return addInspectionMutation(options);
    } else {
      const template = templates.find(({ id }) => id === templateId);

      const documentNumberPlaceholder = !!template?.documentString
        ? `${template.documentString}-00000`
        : '';

      const uuid = uuidv4().toString();
      const mockResult = {
        __typename: 'Inspection',
        id: `${uuid}`,
        templateId,
        vehicleId,
        inspectionType,
        participants,
        reviewers: [],
        regrets: [],
        readyForReview: false,
        workspaceId,
        workspace: null,
        private: privateAssessment,
        labels: [],
        previousStates: [],
        documentNumber: documentNumberPlaceholder,
        subject: null,
        customer: null,
        auditType: null,
        attachments: [],
        completer: null,
        executiveSummary: '',
        followUpInspections: [],
        createdTasks: [],
        audioURL: null,
        status: 'IN_PROGRESS',
        initialInspection: !!conditionalMetadata
          ? {
              id: conditionalMetadata?.initialInspectionId,
              templateId: conditionalMetadata?.initialTemplateId,
            }
          : null,
        reportedWorkspaceId,
        location,
        incidents: [],
      };

      await addMutation(
        inspectionMutation,
        options,
        uuid,
        mockResult,
        ['InspectionForInspectionCreation'],
        'addInspection.id',
      );

      if (!!conditionalMetadata) {
        client.cache.modify({
          id: `Inspection:${conditionalMetadata?.initialInspectionId}`,
          fields: {
            followUpInspections(existingValues = []) {
              return [...existingValues, { id: uuid, templateId }];
            },
          },
        });
      }

      //fetching the fragment from the cache for InspectionTemplate and Vehicle as fragments
      const inspectionTemplate = await client.readFragment({
        id: `InspectionTemplate:${templateId}`, // The ID format should be "typename:id"
        fragment: inspectionTemplateFragment,
      });

      const vehicleFragmentData = await client.readFragment({
        id: `Vehicle:${vehicleId}`,
        fragment: vehicleFragment,
      });

      const participantsFragementData = participants.map((participant) => {
        // Read the fragment for each participant using client.readFragment
        const participantData = client.readFragment({
          id: `User:${participant}`,
          fragment: userFragment,
        });

        return participantData;
      });

      if (
        !inspectionTemplate ||
        !vehicleFragmentData ||
        !participantsFragementData
      ) {
        //return a page saying the inspection cannt be perforemed because you dont have enough data saved or cached to do it offline,
        // inform the user that they might need to make sure they have Template, vehicel and participants data offline before hand
      }

      const mockResultForInspectionList = {
        ...mockResult,
        __typename: 'Inspection',
        id: `${uuid}`,
        passed: true,
        isDraft: true,
        workspaceId: workspace?.id,
        dateCreated: new moment().valueOf().toString(),
        dateModified: new moment().valueOf().toString(),
        riskAnalysis: null,
        inspectionType: inspectionType,
        dateCompleted: null,
        template: inspectionTemplate,
        vehicle: !!vehicleFragmentData ? vehicleFragmentData : null,
        workspace: workspace ?? null,
        creator: currentUser,
        readyForReview: false,
        private: !!privateAssessment,
        labels: [],
        previousStates: [],
        documentNumber: documentNumberPlaceholder,
        notes: '',
        feedbackLike: null,
        feedbackNotes: null,
        actionItems: [],
        timecard: null,
        items: [],
        participants: participantsFragementData,
        subject: subject ?? null,
        customer: null,
        auditType: null,
        attachments: [],
        completer: null,
        odometer: null,
        hours: null,
        audioURL: null,
        reportedWorkspaceId,
        location: location ?? null,
        status: 'IN_PROGRESS',
        incidents: [],
      };

      // Take all the values, and write them to the cache
      // Then you read the values back via the appropriate fragment
      // Then write the fragment result back to the query

      const items = [];

      for (const category of inspectionTemplate.categories) {
        if (category.isBundle) {
          const bundleId = uuidv4();

          for (const question of category.questions) {
            const uuid = uuidv4().toString();

            const addInspectionItemMock = {
              __typename: 'InspectionItem',
              id: `${uuid}`,
              questionId: question.id,
              value: '',
              comments: [],
              failed: false,
              status: '',
              failNotes: null,
              failImage: null,
              user: null,
              users: null,
              additional: null,
              impact: 0,
              probability: 0,
              dateModified: null,
              categoryId: category.id,
              bundleId,
              vehicleId: null,
              vehicle: null,
              actionItemId: null,
            };

            items.push(addInspectionItemMock);
          }
        }
      }

      const mockResultWithAdditionalFields = {
        inspections: [
          {
            ...mockResult,
            __typename: 'Inspection',
            id: `${uuid}`,
            dateCreated: new moment().valueOf().toString(),
            dateModified: new moment().valueOf().toString(),
            dateCompleted: null,
            inspectionType: inspectionType,
            participants: participantsFragementData,
            reviewers: [],
            regrets: [],
            readyForReview: false,
            template: inspectionTemplate,
            vehicle: !!vehicleFragmentData ? vehicleFragmentData : null,
            isDraft: true,
            notes: null,
            riskAnalysis: null,
            actionItems: [],
            labels: [],
            approvals: [],
            items,
            creator: currentUser,
            passed: true,
            timecard: null,
            private: !!privateAssessment,
            feedbackLike: null,
            feedbackNotes: null,
            workspaceId: null,
            workspace: null,
            previousStates: [],
            documentNumber: documentNumberPlaceholder,
            odometer: null,
            hours: null,
            audioURL: null,
            reportedWorkspaceId,
            location: location ?? null,
          },
        ],
      };

      client.writeQuery({
        query: inspectionQuery,
        variables: returnInspectionQueryVaribales(uuid),
        data: mockResultWithAdditionalFields,
      });

      const inspectionListVariables = {
        options: {
          filters: [
            { field: 'isArchived', operator: 'eq', value: ['FALSE'] },
            {
              field: 'creatorId',
              operator: 'eq',
              value: [`${currentUser.id}`],
            },
          ],
          sort: [{ field: 'dateModified', order: 'desc' }],
          pageSize: 20,
        },
      };
      const inspectionsList = await client.readQuery({
        query: inspectionQuery,
        variables: inspectionListVariables,
      });

      client.writeQuery({
        query: inspectionQuery,
        variables: inspectionListVariables,
        data: {
          inspections: [
            mockResultForInspectionList,
            ...(inspectionsList?.inspections || []),
          ],
          inspectionsCount: inspectionsList?.inspectionCount + 1 || 1,
        },
      });

      return new Promise((resolve, reject) => {
        const responseData = {
          data: {
            addInspection: { id: uuid },
          },
        };
        resolve(responseData);
      });
    }
  };

  const updateInspectionTestResults = async ({
    inspectionId,
    itemId,
    value,
    failed,
    status,
    failNotes,
    failImage,
    userId,
    additional,
    impact,
    probability,
    questionId,
    inputType,
    vehicleId,
    actionItemId,
  }) => {
    const options = {
      variables: {
        id: itemId,
        value,
        failed,
        status,
        failNotes,
        failImage,
        userId: userId ? userId : null,
        additional: additional ? additional : null,
        impact: impact ? impact : 0,
        probability: probability ? probability : 0,
        vehicleId,
        actionItemId,
      },
    };

    if (online) {
      // Optimistic responses use a UUID as a placeholder id until the add
      // mutation resolves. If the user answers again during that window the
      // update path is taken with a UUID, which the backend cannot parse as
      // an Int. Guard here so we never fire an update with a non-numeric id.
      if (!itemId || isNaN(Number(itemId))) {
        return Promise.resolve({ data: { updateInspectionItem: null } });
      }
      return updateInspectionItemMutation(options);
    } else {
      const uuid = uuidv4().toString();
      const mockResult = {
        __typename: 'InspectionItem',
        id: itemId,
        value,
        failed,
        status,
        failNotes,
        failImage,
        userId: userId ? userId : null,
        additional: additional ? additional : null,
        impact: impact ? impact : 0,
        probability: probability ? probability : 0,
        questionId: questionId,
        inputType: inputType,
        categoryId: null,
        bundleId: null,
        vehicleId,
        comments: [],
        vehicle: null,
        actionItemId: null,
      };
      //the last field is empty array because in update we are not createing anythig so we dont need to actually find and delete it.
      await addMutation(
        inspectionItemUpdateMutation,
        options,
        uuid,
        mockResult,
        ['InspectionForInspectionCreation'],
        '',
      );

      const oldData = await client.readQuery({
        query: inspectionQuery,
        variables: returnInspectionQueryVaribales(inspectionId),
      });
      const vehicle = workspaceVehicles?.find((v) => v.id === vehicleId);

      const newData = produce(oldData, (draft) => {
        if (
          draft.inspections &&
          draft.inspections[0] &&
          draft.inspections[0].items
        ) {
          const foundInspectionItem = draft.inspections[0].items.find(
            (obj) => obj.id === itemId,
          );
          foundInspectionItem.value = value;
          foundInspectionItem.failed = failed;
          foundInspectionItem.status = status;
          foundInspectionItem.failNotes = failNotes ?? null;
          foundInspectionItem.failImage = failImage ?? null;
          foundInspectionItem.additional = additional ? additional : null;
          foundInspectionItem.impact = impact ? impact : 0;
          foundInspectionItem.probability = probability ? probability : 0;
          foundInspectionItem.dateModified = null;
          foundInspectionItem.vehicle = vehicle ?? null;
          foundInspectionItem.actionItem = null;

          if (draft?.inspections[0]?.passed && failed) {
            draft.inspections[0].passed = !failed;
          }
        }
      });

      await client.writeQuery({
        query: inspectionQuery,
        variables: returnInspectionQueryVaribales(inspectionId),
        data: newData,
      });
      //edit the promise later
      return new Promise((resolve, reject) => {
        const responseData = {
          data: {
            updateInspectionItem: '',
          },
        };
        resolve(responseData);
      });
    }
  };

  const addInspectionTestResults = async ({
    inspectionId,
    questionId,
    value,
    failed,
    status,
    failNotes,
    failImage,
    userId,
    additional,
    impact,
    probability,
    inputType,
    vehicleId,
    parentBundleId,
    actionItemId,
  }) => {
    const options = {
      variables: {
        inspectionId,
        questionId,
        value,
        failed,
        status,
        failNotes,
        failImage,
        userId: userId || null,
        additional,
        impact,
        probability,
        vehicleId,
        parentBundleId,
        actionItemId,
      },
    };

    if (online) {
      const optimisticId = uuidv4();

      const optimisticInspectionItem = {
        __typename: 'InspectionItem',
        id: optimisticId,
        inspectionId,
        questionId,
        value,
        failed,
        status,
        failNotes: failNotes != null ? failNotes : null,
        failImage: failImage != null ? failImage : null,
        userId: userId || null,
        additional: additional != null ? additional : null,
        impact: impact != null ? impact : 0,
        probability: probability != null ? probability : 0,
        vehicleId: vehicleId || null,
        actionItemId: actionItemId || null,
        parentBundleId: parentBundleId || null,
        dateCreated: new Date().toISOString(),
        dateModified: null,
        creator: null,
        user: null,
        bundleId: null,
        categoryId: null,
        users: [],
        comments: [],
        vehicle: null,
        riskCategory: null,
        riskScore: 0,
        riskIdentificationFeedback: false,
        combinedQuestionAnswer: '',
      };

      options.optimisticResponse = {
        addInspectionItem: optimisticInspectionItem,
      };

      options.update = (cache, { data: { addInspectionItem } }) => {
        try {
          const data = cache.readQuery({
            query: inspectionQuery,
            variables: returnInspectionQueryVaribales(inspectionId),
          });

          const currentItems = data?.inspections[0]?.items || [];

          const newItems = [...currentItems, addInspectionItem].reduce(
            (acc, item) => {
              if (!acc.find((existing) => existing.id === item.id)) {
                acc.push(item);
              }
              return acc;
            },
            [],
          );

          const updatedInspection = {
            ...data.inspections[0],
            items: newItems,
          };

          cache.writeQuery({
            query: inspectionQuery,
            variables: returnInspectionQueryVaribales(inspectionId),
            data: { inspections: [updatedInspection] },
          });
        } catch (error) {
          console.error('Error updating inspection item in cache:', error);
        }
      };

      return addInspectionItemMutation(options);
    } else {
      const uuid = uuidv4().toString();
      const mockResult = {
        __typename: 'InspectionItem',
        id: `${uuid}`,
        inspectionId,
        questionId,
        value,
        failed,
        status,
        failNotes,
        failImage,
        userId: userId || null,
        additional,
        impact,
        probability,
        inputType: inputType,
        categoryId: null,
        bundleId: null,
        vehicleId,
        vehicle: null,
        comments: [],
        actionItemId: null,
        parentBundleId,
      };
      await addMutation(
        inspectionItemMutation,
        options,
        uuid,
        mockResult,
        ['InspectionForInspectionCreation'],
        'addInspectionItem.id',
      );

      const oldData = await client.readQuery({
        query: inspectionQuery,
        variables: returnInspectionQueryVaribales(inspectionId),
      });

      const vehicle = workspaceVehicles?.find((v) => v.id === vehicleId);

      const addInspectionItemMock = {
        __typename: 'InspectionItem',
        id: `${uuid}`,
        questionId,
        value,
        failed,
        status,
        failNotes: failNotes ? failNotes : null,
        failImage: failImage ? failImage : null,
        user: null,
        users: null,
        additional: additional ? additional : null,
        impact: impact ? impact : 0,
        probability: probability ? probability : 0,
        dateModified: null,
        bundleId: null,
        categoryId: null,
        comments: [],
        vehicle: vehicle ?? null,
        actionItemId: null,
        parentBundleId: parentBundleId ?? null,
      };

      const newData = produce(oldData, (draft) => {
        draft.inspections[0].items = [
          ...draft.inspections[0].items,
          addInspectionItemMock,
        ];
        draft.inspections[0].passed = !failed;
      });

      await client.writeQuery({
        query: inspectionQuery,
        variables: returnInspectionQueryVaribales(inspectionId),
        data: newData,
      });

      return new Promise((resolve, reject) => {
        const responseData = {
          data: {
            addInspectionItem: addInspectionItemMock,
          },
        };
        resolve(responseData);
      });
    }
  };

  const deleteInspectionItemUsersResults = async (
    userId,
    inspectionId,
    _questionId,
    itemId,
  ) => {
    const options = {
      variables: {
        userId: userId,
        inspectionId: inspectionId,
        itemId,
      },
    };

    if (online) {
      return deleteInspectionItemUsers(options);
    } else {
      const uuid = uuidv4().toString();
      //instead of mockResults we have null here because when we are deleting there is nothing to delete after we go online so no mock results
      await addMutation(
        deleteInspectionItemUsersMutation,
        options,
        uuid,
        null,
        ['InspectionForInspectionCreation'],
        '',
      );

      const oldData = await client.readQuery({
        query: inspectionQuery,
        variables: returnInspectionQueryVaribales(inspectionId),
      });

      const newData = produce(oldData, (draft) => {
        if (
          draft.inspections &&
          draft.inspections[0] &&
          draft.inspections[0].items
        ) {
          let foundInspectionItem = draft.inspections[0].items.find(
            (obj) => obj.id === itemId,
          );

          if (foundInspectionItem) {
            foundInspectionItem.users = foundInspectionItem.users.filter(
              (user) => user.id !== userId,
            );
          }
        }
      });

      await client.writeQuery({
        query: inspectionQuery,
        variables: returnInspectionQueryVaribales(inspectionId),
        data: newData,
      });

      return new Promise((resolve, reject) => {
        const responseData = {
          data: {
            deleteInspectionItemUser: {
              id: inspectionId,
              __typename: 'Inspection',
            },
          },
        };
        resolve(responseData);
      });
    }
  };

  const addInspectionItemUsersResults = async (
    userId,
    inspectionId,
    questionId,
    itemId,
  ) => {
    const options = {
      variables: {
        userId: userId,
        inspectionId: inspectionId,
        questionId: questionId,
        inspectionItemId: itemId,
      },
    };

    if (online) {
      return addInspectionItemUsers(options);
    } else {
      //no Mock results as we dont need them cause we are not creating any new item but adding stuff to the one we have
      const uuid = uuidv4().toString();
      await addMutation(
        addInspectionItemUsersMutation,
        options,
        uuid,
        null,
        ['InspectionForInspectionCreation'],
        '',
      );

      const oldData = await client.readQuery({
        query: inspectionQuery,
        variables: returnInspectionQueryVaribales(inspectionId),
      });

      const newData = produce(oldData, (draft) => {
        if (
          draft.inspections &&
          draft.inspections[0] &&
          draft.inspections[0].items
        ) {
          let foundInspectionItem = draft.inspections[0].items.find(
            (obj) => obj.id === itemId,
          );

          const addedUserFragmentData = client.readFragment({
            id: `User:${userId}`,
            fragment: userFragment,
          });

          if (!foundInspectionItem.users) {
            foundInspectionItem.users = []; // Ensure users array exists
          }

          foundInspectionItem.users.push(addedUserFragmentData);
        }
      });

      await client.writeQuery({
        query: inspectionQuery,
        variables: returnInspectionQueryVaribales(inspectionId),
        data: newData,
      });

      return new Promise((resolve, reject) => {
        const responseData = {
          data: {
            deleteInspectionItemUser: {
              id: inspectionId,
              __typename: 'Inspection',
            },
          },
        };
        resolve(responseData);
      });
    }
  };

  const isQuotaError = (err) =>
    err?.name === 'QuotaExceededError' ||
    err?.code === 22 ||
    (err?.message && /quota/i.test(err.message));

  const purgeSwRuntimeCaches = () =>
    new Promise((resolve) => {
      const controller = navigator.serviceWorker?.controller;
      if (!controller) return resolve(false);
      const channel = new MessageChannel();
      const timer = setTimeout(() => resolve(false), 3000);
      channel.port1.onmessage = ({ data }) => {
        clearTimeout(timer);
        resolve(data?.ok === true);
      };
      controller.postMessage({ type: 'PURGE_RUNTIME_CACHES' }, [channel.port2]);
    });

  const safeAddOfflineFile = async (id, file) => {
    try {
      await addOfflineFile(id, file);
      return true;
    } catch (err) {
      if (!isQuotaError(err)) {
        showToast({
          title: 'File Save Failed',
          message: 'Could not save file for offline use. Please try again.',
          variant: toastVariant.danger,
          time: toastLength.lg,
        });
        console.error('[offline] addOfflineFile failed:', err);
        return false;
      }

      // Quota hit — purge recoverable SW runtime caches and retry once.
      console.warn(
        '[offline] Quota exceeded, purging SW runtime caches and retrying...',
      );
      await purgeSwRuntimeCaches();

      try {
        await addOfflineFile(id, file);
        return true;
      } catch (retryErr) {
        showToast({
          title: 'Storage Full',
          message: 'Your device storage is full. Free up space and try again.',
          variant: toastVariant.danger,
          time: toastLength.lg,
        });
        console.error(
          '[offline] addOfflineFile failed after cache purge:',
          retryErr,
        );
        return false;
      }
    }
  };

  const addImageResults = async (
    description,
    onSubmit,
    file,
    signatureDataText = null,
  ) => {
    const usableFile = await convertHeicIfNeeded(file);
    const compressed = await compressImage(usableFile);

    if (!signatureDataText) {
      const fileUUID = uuidv4();
      if (online) {
        uploadImageFile(
          fileUUID,
          compressed,
          description,
          onSubmit,
          false,
          false,
          null,
          null,
        );
      } else {
        const saved = await safeAddOfflineFile(fileUUID, compressed);
        if (saved) onSubmit(fileUUID, description);
      }
    } else if (signatureDataText) {
      const fileParts = file.name.split('.');
      const fileName = fileParts[0];

      if (online) {
        uploadImageFile(
          fileName,
          compressed,
          description,
          onSubmit,
          false,
          signatureDataText,
          null,
          null,
        );
      } else {
        const saved = await safeAddOfflineFile(fileName, compressed);
        if (saved) {
          const newAdditional = signatureDataText.originalAdditional?.length
            ? signatureDataText.originalAdditional.concat(
                '|' + signatureDataText.signatureText,
              )
            : signatureDataText.signatureText;
          onSubmit(fileName, false, 'PASS', newAdditional);
        }
      }
    }
  };

  const addUploadFileResults = async (
    onSubmit,
    file,
    filename,
    previousAdditional,
  ) => {
    const fileUUID = uuidv4();
    if (online) {
      uploadImageFile(
        fileUUID,
        file,
        null,
        onSubmit,
        false,
        null,
        filename,
        previousAdditional,
      );
    } else {
      const saved = await safeAddOfflineFile(fileUUID, file);
      if (saved) {
        const newAdditional = previousAdditional
          ? `${previousAdditional}|${filename}`
          : `${filename}`;
        onSubmit({
          ...file,
          url: fileUUID,
          name: newAdditional,
        });
      }
    }
  };

  //Note: The onSubmit function default value is empty function because when we are doing offline we don't need onSubmit.
  //Also same goes for isOfflineImage because its value remains false until you need offline image upload.
  //The onSubmit and isOfflineImage, in any case one of them will have a default value.
  //When onlinerequest then onsubmit will be somehting
  const uploadImageFile = async (
    fileUUID,
    file,
    description,
    onSubmit = () => {},
    isOfflineImage = false,
    signatureDataText = null,
    nonImageFileName = null,
    previousAdditional = null,
  ) => {
    const fileName = fileUUID;
    const fileType = getFileExtension(file);

    try {
      const { data } = await client.query({
        query: uploadUrlQuery,
        variables: {
          fileName: `assets/${currentUser.company.id}/${fileName}.${fileType}`,
          fileType,
        },
      });

      const signedUrl = data.simpleStorageUploadUrl;
      const options = {
        headers: {
          'Content-Type': fileType === 'pdf' ? 'application/pdf' : fileType,
        },
      };
      const result = await axios.put(signedUrl, file, options);

      if (result.status === 200) {
        const imageUrl = `${CF_DOMAIN(currentUser)}${fileName}.${fileType}`;
        if (signatureDataText) {
          const newAdditional = signatureDataText.originalAdditional?.length
            ? signatureDataText.originalAdditional.concat(
                '|' + signatureDataText.signatureText,
              )
            : signatureDataText.signatureText;
          if (!isOfflineImage) {
            onSubmit(imageUrl, false, 'PASS', newAdditional);
          }
        } else if (nonImageFileName) {
          const newAdditional = previousAdditional
            ? `${previousAdditional}|${nonImageFileName}`
            : `${nonImageFileName}`;
          axios.put(signedUrl, file, options).then((result) => {
            if (result.status === 200) {
              onSubmit({
                size: file?.size,
                fileType: file?.type,
                extension: fileType,
                url: imageUrl,
                name: newAdditional,
              });
            }
          });
        } else {
          if (!isOfflineImage) {
            onSubmit(imageUrl, description);
          }
        }
        return imageUrl;
      }
    } catch (error) {
      throw new Error(`Upload failed: ${error}`);
    }
  };

  const completeInspection = async (inspectionId) => {
    const options = {
      variables: {
        id: inspectionId,
      },
    };
    if (online) {
      return await publishInspection(options);
    } else {
      const uuid = uuidv4().toString();
      const mockResult = {
        __typename: 'InspectionItem',
        id: `${uuid}`,
      };

      await addMutation(
        publishMutation,
        options,
        uuid,
        mockResult,
        ['InspectionForInspectionCreation'],
        '',
      );
      const oldData = await client.readQuery({
        query: inspectionQuery,
        variables: returnInspectionQueryVaribales(inspectionId),
      });

      const newData = produce(oldData, (draft) => {
        if (draft.inspections && draft.inspections[0]) {
          draft.inspections[0].isDraft = false;
          draft.inspections[0].status = 'COMPLETE';
          draft.inspections[0].dateCompleted = Date.now();
          draft.inspections[0].completerId = currentUser.id;
          draft.inspections[0].completer = currentUser;
        }
      });

      const inspectionListVariables = {
        options: {
          filters: [
            { field: 'isArchived', operator: 'eq', value: ['FALSE'] },
            {
              field: 'creatorId',
              operator: 'eq',
              value: [`${currentUser.id}`],
            },
          ],
          sort: [{ field: 'dateModified', order: 'desc' }],
          pageSize: 20,
        },
      };
      const inspectionsList = await client.readQuery({
        query: inspectionQuery,
        variables: inspectionListVariables,
      });

      // Write updated status back to individual inspection cache entry
      client.writeQuery({
        query: inspectionQuery,
        variables: returnInspectionQueryVaribales(inspectionId),
        data: newData,
      });

      const inspectionListFormatted = [
        ...(inspectionsList?.inspections || []),
      ].filter((i) => i?.id !== newData?.inspections[0]?.id);

      client.writeQuery({
        query: inspectionQuery,
        variables: inspectionListVariables,
        data: {
          inspections: [newData.inspections[0], ...inspectionListFormatted],
          inspectionsCount: inspectionsList?.inspectionCount + 1 || 1,
        },
      });
      syncFromCache();
      const route = getRoute(workspace.id, `${paths.dashboard}`);
      navigate(route);
    }
  };

  const uploadAudioFile = async ({
    fileUUID,
    blob,
    onSubmit = () => {},
    setFile = () => {},
    setLoading = () => {},
    recordingName,
    transcriptRef,
  }) => {
    setLoading(true);
    if (!validateBlob(blob)) return;

    const fileType = blob.type;
    const fileName = `assets/${currentUser?.company?.id}/${fileUUID}.${fileType}`;
    const url = `${CF_DOMAIN(currentUser)}${fileUUID}.${fileType}`;

    try {
      const { data } = await client.query({
        query: uploadUrlQuery,
        variables: { fileName, fileType },
      });

      if (!data.simpleStorageUploadUrl) {
        console.error('Failed to retrieve signed URL');
        return;
      }

      await axios.put(data.simpleStorageUploadUrl, blob, {
        headers: { 'Content-Type': blob.type },
      });

      onSubmit(transcriptRef.current, url);
      setFile({ url, name: recordingName });
      setLoading(false);
      return url;
    } catch (error) {
      console.error('Error during file upload:', error);
    } finally {
      setLoading(false);
    }
  };

  const addAudioFileResults = async ({
    file,
    transcriptRef,
    onSubmit,
    setFile,
    setLoading,
    recordingName,
  }) => {
    const fileUUID = uuidv4();

    const audioFileDetails = {
      blob: file,
      fileUUID,
      onSubmit,
      transcriptRef,
      recordingName,
      setFile,
      setLoading,
    };

    if (online) {
      return await uploadAudioFile(audioFileDetails);
    } else {
      setLoading(true);
      if (!validateBlob(file)) {
        setLoading(false);
        return;
      }

      const saved = await safeAddOfflineFile(fileUUID, file);
      if (saved) {
        onSubmit(transcriptRef.current, fileUUID);
        setFile({ url: fileUUID, name: recordingName });
      }
      setLoading(false);
      return saved ? fileUUID : undefined;
    }
  };
  const validateBlob = (blob) => {
    if (!blob || !blob.size || !blob.type) {
      console.error('Invalid blob:', blob);
      return false;
    }
    return true;
  };

  const addParticipant = async (variables) => {
    if (online) {
      return addParticipantMut({ variables });
    } else {
      const uuid = uuidv4().toString();

      const oldData = client.readQuery({
        query: inspectionQuery,
        variables: returnInspectionQueryVaribales(variables.inspectionId),
      });

      const participants = oldData.inspections[0].participants;

      const userToAdd = allUsers.find(({ id }) => id === variables.userId);

      const newParticipants = [...participants, userToAdd];

      client.cache.writeQuery({
        variables: returnInspectionQueryVaribales(variables.inspectionId),
        data: {
          inspections: [
            {
              ...oldData.inspections[0],
              participants: [...newParticipants],
            },
          ],
        },
        query: inspectionQuery,
      });

      const mockResult = {
        id: variables.inspectionId,
        participants: [...newParticipants],
      };

      await addMutation(
        addParticipantMutation,
        { variables },
        uuid,
        mockResult,
        [''],
        '',
      );

      return new Promise((resolve, reject) => {
        const responseData = {
          data: {
            addInpsectionParticipant: { ...mockResult },
          },
        };
        resolve(responseData);
      });
    }
  };

  const addCategoryStatus = async (variables) => {
    if (online) {
      return addCategoryStatusMut({ variables });
    } else {
      const uuid = uuidv4().toString();
      const nowIso = new Date().toISOString();
      const cacheVars = { inspectionId: String(variables.inspectionId) };

      const GET_INSPECTION_CATEGORY_STATUS = gql`
        query GetInspectionCategoryStatus($inspectionId: String!) {
          inspectionCategoryStatus(
            options: {
              filters: [
                { field: "inspectionId", operator: "eq", value: $inspectionId }
              ]
            }
          ) {
            id
            status
            categoryId
            inspectionId
            dateCreated
            creator {
              id
              firstName
              lastName
              avatarUrl
            }
          }
        }
      `;

      let oldData;
      try {
        oldData = client.readQuery({
          query: GET_INSPECTION_CATEGORY_STATUS,
          variables: cacheVars,
        });
      } catch {
        oldData = undefined;
      }

      const existing = oldData?.inspectionCategoryStatus?.[0];
      const mockResult = existing
        ? {
            ...existing,
            status: variables.status,
            categoryId: variables.categoryId,
            dateModified: nowIso,
          }
        : {
            __typename: 'InspectionCategoryStatus',
            id: uuid,
            inspectionId: String(variables.inspectionId),
            categoryId: variables.categoryId,
            status: variables.status,
            dateCreated: nowIso,
            dateModified: nowIso,
            creator: {
              __typename: 'User',
              id: currentUser?.id,
              firstName: currentUser?.firstName || '',
              lastName: currentUser?.lastName || '',
              avatarUrl: currentUser?.avatarUrl || '',
            },
          };

      client.cache.writeQuery({
        query: GET_INSPECTION_CATEGORY_STATUS,
        variables: cacheVars,
        data: {
          inspectionCategoryStatus: [mockResult],
        },
      });

      await addMutation(
        addCategoryStatusMutation,
        { variables },
        uuid,
        mockResult,
        ['GetInspectionCategoryStatus'],
        '',
      );

      return Promise.resolve({
        data: { addInspectionCategoryStatus: mockResult },
      });
    }
  };
  return {
    addInspection,
    addInspectionTestResults,
    updateInspectionTestResults,
    deleteInspectionItemUsersResults,
    addInspectionItemUsersResults,
    addImageResults,
    uploadImageFile,
    addUploadFileResults,
    completeInspection,
    updateNotes,
    addAudioFileResults,
    uploadAudioFile,
    updateBundle,
    removeBundle,
    updateAsset,
    updateInspectionUsage,
    addParticipant,
    addCategoryStatus,
  };
};
